import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ref } from 'vue'
import { useToast } from 'vue-toastification'

import useCustomMutation from '@/composables/useCustomMutation'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { MutationParams } from '@/interfaces/mutationParams'
import type { ServiceCharge } from '@/interfaces/serviceCharge'

import { updateCompany } from './useCompany'

interface Props {
	params: Params
	immediate?: boolean
}

interface Params extends Omit<Partial<ServiceCharge>, 'id'> {
	id: string | number
	companyId?: number
}

interface UpdateParams extends Params {}

const getServiceCharge = async (params?: Params) => {
	if (params) {
		const { data } = await axios.get<ServiceCharge[]>(
			`/api/companies/${params.companyId}/service-charges`,
			{ errorHandling: false } as CustomAxiosRequestConfig
		)
		return data
	}
}

const createServiceCharge = async (params: ServiceCharge) => {
	const { data } = await axios.post<ServiceCharge>(
		`/api/companies/${params.companyId}/service-charges`,
		params
	)
	return data
}

const updateServiceCharge = async (params: UpdateParams) => {
	const { data } = await axios.put<ServiceCharge>(
		`/api/service-charges/${params.id}`,
		params
	)
	return data
}

const useCompanyServiceCharge = (props?: Props) => {
	const { params, immediate = true } = props || {}
	const toast = useToast()

	const companyId = ref<number | undefined>(params?.companyId)

	const { data, isLoading, refetch, isFetching, isInitialLoading } = useQuery(
		['companyServiceCharge', params?.id],
		() =>
			getServiceCharge({
				companyId: companyId.value,
				id: companyId.value!,
			}),
		{
			enabled: params ? immediate : false,
			retry: false,
		}
	)

	const createMutation = useMutation(
		(mutationParams: MutationParams<ServiceCharge>) =>
			createServiceCharge(mutationParams.params),
		useCustomMutation(toast, 'create')
	)

	const updateMutation = useMutation(
		(mutationParams: MutationParams<UpdateParams>) =>
			updateServiceCharge(mutationParams.params),
		useCustomMutation(toast, 'update')
	)

	return {
		companyId,
		data,
		isLoading,
		isFetching,
		isInitialLoading,

		refetch: () => params && refetch(),

		createMutation,
		createCompanyServiceChargeMutation: createMutation,
		createCompanyServiceCharge: createMutation.mutate,
		createCompanyServiceChargeAsync: createMutation.mutateAsync,
		createCompanyServiceChargeLoading: createMutation.isLoading,

		updateMutation,
		updateCompanyServiceChargeMutation: updateMutation,
		updateCompanyServiceCharge: updateMutation.mutate,
		updateCompanyServiceChargeAsync: updateMutation.mutateAsync,
		updateCompanyServiceChargeLoading: updateMutation.isLoading,
	}
}

export default useCompanyServiceCharge
